import React, { useState } from 'react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import api from '../config/api';

const StripePaymentForm = ({ shippingInfo, cartItems, subtotal, shippingCost, total, clearCart, navigate }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [orderError, setOrderError] = useState('');
  const [orderLoading, setOrderLoading] = useState(false);
  const [nameOnCard, setNameOnCard] = useState('');

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setOrderLoading(true);
    setOrderError('');

    try {
      // 1. Get client secret from our backend
      const { data: intentData } = await api.post('/create-payment-intent.php', {
        total: total
      });

      if (!intentData.success || !intentData.clientSecret) {
        throw new Error(intentData.error || 'Failed to initialize payment.');
      }

      // 2. Confirm the card payment with Stripe
      const cardElement = elements.getElement(CardElement);
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(intentData.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: nameOnCard || `${shippingInfo.firstName} ${shippingInfo.lastName}`,
            email: shippingInfo.email,
            phone: shippingInfo.phone,
            address: {
              city: shippingInfo.city,
              country: 'CA',
              line1: shippingInfo.address,
              postal_code: shippingInfo.postalCode,
              state: shippingInfo.province,
            }
          },
        },
      });

      if (stripeError) {
        throw new Error(stripeError.message);
      }

      if (paymentIntent.status !== 'succeeded') {
        throw new Error('Payment was not successful. Status: ' + paymentIntent.status);
      }

      // 3. Payment succeeded! Now save the order to our database
      const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });

      const builtCartItems = await Promise.all(cartItems.map(async (item) => {
        let engravingBase64 = null;
        let engravingFileName = null;
        if (item.engravingData?.file) {
          try {
            engravingBase64 = await toBase64(item.engravingData.file);
            engravingFileName = item.engravingData.file.name;
          } catch (_) {}
        }
        return {
          name:             item.product.fullName,
          quantity:         item.quantity,
          pricePerBox:      item.product.pricePerBox,
          lineTotal:        item.product.pricePerBox * item.quantity,
          engravingImage:   engravingBase64,
          engravingFileName: engravingFileName,
          subscription:     item.subscription?.label || null,
        }
      }));

      // We only store that it was a Stripe payment, not actual card digits
      await api.post('/checkout.php', {
        shipping: shippingInfo,
        cartItems: builtCartItems,
        subtotal: subtotal,
        shippingCost: shippingCost,
        total: total,
        cardLast4: 'Stripe', // Using Stripe instead of manual digits
      });

      clearCart();
      navigate('/order-confirmation');

    } catch (err) {
      setOrderError(err.message || 'An unexpected error occurred during checkout.');
    } finally {
      setOrderLoading(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true, // We already collect postal code in shipping step
  };

  return (
    <form onSubmit={handlePlaceOrder} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="p-6 border-b border-gray-100 flex items-center gap-3">
        <ShieldCheckIcon className="w-5 h-5 text-primary-600" />
        <h2 className="font-bold text-gray-900 text-lg">Secure Payment</h2>
      </div>
      <div className="p-6 space-y-5">
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
          Payments are securely processed by Stripe. We do not store your credit card details.
        </div>
        
        <div>
          <label htmlFor="pay-name" className="block text-xs font-semibold text-gray-700 mb-1.5">Name on Card *</label>
          <input 
            id="pay-name" 
            name="name" 
            required 
            value={nameOnCard} 
            onChange={(e) => setNameOnCard(e.target.value)}
            placeholder="Dr. Jane Smith"
            className="w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:ring-2 focus:ring-primary-300 focus:border-primary-400 outline-none" 
          />
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Card Details *</label>
          <div className="w-full rounded-xl border border-gray-200 px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-primary-300 focus-within:border-primary-400">
            <CardElement options={cardElementOptions} />
          </div>
        </div>
      </div>
      
      <div className="p-6 border-t border-gray-100 space-y-4 bg-gray-50">
        {orderError && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl">
            {orderError}
          </div>
        )}
        <button 
          type="submit" 
          disabled={!stripe || orderLoading}
          className="w-full py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-primary-400 text-white font-bold text-base rounded-xl flex items-center justify-center gap-2 transition-colors shadow-lg"
        >
          {orderLoading ? (
            <><div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />Processing securely...</>
          ) : (
            <><ShieldCheckIcon className="w-5 h-5" />Pay ${(total).toFixed(2)} CAD securely</>
          )}
        </button>
        <p className="text-center text-xs text-gray-400 mt-3">
          By placing your order you agree to our terms and conditions.
        </p>
      </div>
    </form>
  );
};

export default StripePaymentForm;
